import { Link } from 'react-router-dom';
import {
  escapeHtml,
  escapeHtmlAttribute,
  inlineMarkdownToHtml,
  parseMarkdownTable,
  vimeoVideoId,
  youtubeVideoId,
} from './markdown';

export type BooksRawBlock = Record<string, unknown>;

function coerceString(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value === 'string') {
    const s = value.trim();
    return s.length ? s : null;
  }
  if (typeof value === 'number' || typeof value === 'boolean') return value.toString();
  return null;
}

function isSafeUrl(href: string): boolean {
  const t = href.trim().toLowerCase();
  return (
    t.startsWith('https://') ||
    t.startsWith('http://') ||
    t.startsWith('mailto:') ||
    (t.startsWith('/') && !t.startsWith('//'))
  );
}

/**
 * Rich blocks from Al Muslim Books / announcements — same schema as `announcementBodyHtml` blocks.
 */
export function BooksBlocks(props: {
  blocks: unknown[];
  intro?: string | null;
  /** compact = fewer vertical rhythms (for “full” depth); expanded for study */
  density?: 'compact' | 'expanded';
}) {
  const { blocks, intro, density = 'expanded' } = props;
  const cls = density === 'compact' ? 'wi-books wi-books--compact' : 'wi-books';

  return (
    <article className={`${cls} ann-rich books-mk-chapter`}>
      {intro ? (
        <div
          className="ann-lead wi-books-intro"
          dangerouslySetInnerHTML={{ __html: formatIntroHtml(intro) }}
        />
      ) : null}
      {(blocks as BooksRawBlock[]).map((raw, i) => (
        <BooksBlock key={i} raw={raw} />
      ))}
    </article>
  );
}

function formatIntroHtml(text: string): string {
  const t = (text || '').trim();
  if (!t) return '';
  const parts = t.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
  if (parts.length <= 1) {
    return `<p class="ann-p">${escapeHtml(t).replace(/\n/g, '<br>')}</p>`;
  }
  return parts.map((p) => `<p class="ann-p">${escapeHtml(p).replace(/\n/g, '<br>')}</p>`).join('');
}

function BooksBlock({ raw }: { raw: BooksRawBlock }) {
  if (!raw || typeof raw !== 'object') return null;
  let type = String(raw.type || 'paragraph').toLowerCase();
  const text = coerceString(raw.text) ?? '';
  const imageUrl = coerceString(raw.imageUrl ?? raw.image_url) ?? '';
  const btnText = coerceString(raw.buttonText ?? raw.button_text) ?? '';
  const btnUrl = coerceString(raw.buttonUrl ?? raw.button_url) ?? '';
  const caption = coerceString(raw.caption) ?? '';
  const alt = coerceString(raw.alt) ?? '';
  const embedUrl = coerceString(raw.embedUrl ?? raw.url) ?? '';
  const calloutVariant = coerceString(raw.variant) ?? 'default';

  if (type === 'text') type = 'paragraph';

  if (type === 'paragraph') {
    const rich = raw.rich === true || raw.rich === 'true';
    if (rich && text) {
      return (
        <p
          className="ann-block ann-p ann-rich wi-books-p"
          dangerouslySetInnerHTML={{ __html: inlineMarkdownToHtml(text) }}
        />
      );
    }
    if (text) {
      return (
        <p className="ann-block ann-p wi-books-p">
          {text.split('\n').map((line, i) => (
            <span key={i}>
              {i > 0 ? <br /> : null}
              {line}
            </span>
          ))}
        </p>
      );
    }
    return null;
  }

  if (type === 'heading_1') {
    return <h2 className="ann-block ann-h1 books-mk-chapter-title wi-books-h1">{text}</h2>;
  }
  if (type === 'heading_2') {
    return <h3 className="ann-block ann-h2 wi-books-h2">{text}</h3>;
  }
  if (type === 'heading_3') {
    return <h4 className="ann-block ann-h3 wi-books-h3">{text}</h4>;
  }
  if (type === 'quote') {
    return (
      <blockquote className="ann-block ann-quote wi-books-quote">
        {text.split('\n').map((line, i) => (
          <span key={i}>
            {i > 0 ? <br /> : null}
            {line}
          </span>
        ))}
      </blockquote>
    );
  }
  if (type === 'code') {
    return (
      <pre className="ann-block ann-code wi-books-code">
        <code>{text}</code>
      </pre>
    );
  }
  if (type === 'bulleted_list' || type === 'numbered_list' || type === 'checklist') {
    const items = text.split('\n').map((s) => s.trim()).filter(Boolean);
    const Tag = type === 'numbered_list' ? 'ol' : 'ul';
    return (
      <Tag className="ann-block ann-list wi-books-list">
        {items.map((line, idx) => (
          <li key={idx}>
            {type === 'checklist' ? (
              <>
                <input type="checkbox" disabled readOnly /> {line}
              </>
            ) : (
              line
            )}
          </li>
        ))}
      </Tag>
    );
  }
  if (type === 'divider') {
    return <hr className="ann-block ann-hr wi-books-hr" />;
  }
  if (type === 'toggle') {
    const nl = text.indexOf('\n');
    const sum = (nl === -1 ? text : text.slice(0, nl)).trim() || 'Details';
    const rest = (nl === -1 ? '' : text.slice(nl + 1)).trim();
    return (
      <details className="ann-block ann-toggle wi-books-toggle" open={!rest}>
        <summary className="ann-toggle-sum">{sum}</summary>
        {rest ? (
          <div className="ann-toggle-body">
            {rest.split('\n').map((line, i) => (
              <span key={i}>
                {i > 0 ? <br /> : null}
                {line}
              </span>
            ))}
          </div>
        ) : null}
      </details>
    );
  }
  if (type === 'image') {
    if (!imageUrl) return null;
    const altText = (alt || caption || 'Image').trim();
    return (
      <figure className="ann-block ann-fig wi-books-fig">
        <img className="ann-img" src={imageUrl} alt={altText} loading="lazy" decoding="async" />
        {caption ? <figcaption className="ann-figcap">{caption}</figcaption> : null}
      </figure>
    );
  }
  if (type === 'table') {
    const tbl = parseMarkdownTable(text);
    if (!tbl) return null;
    return <div className="ann-block ann-table-wrap wi-books-table-wrap" dangerouslySetInnerHTML={{ __html: tbl }} />;
  }
  if (type === 'video_embed' || type === 'video') {
    const u = embedUrl || coerceString(raw.videoUrl ?? raw.video_url) || '';
    if (!u || !isSafeUrl(u)) return null;
    const yt = youtubeVideoId(u);
    const vm = vimeoVideoId(u);
    if (yt) {
      return (
        <div className="ann-block ann-video wi-books-video">
          <div className="ann-video-inner">
            <iframe
              className="ann-video-frame"
              src={`https://www.youtube-nocookie.com/embed/${escapeHtmlAttribute(yt)}`}
              title="YouTube video"
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      );
    }
    if (vm) {
      return (
        <div className="ann-block ann-video wi-books-video">
          <div className="ann-video-inner">
            <iframe
              className="ann-video-frame"
              src={`https://player.vimeo.com/video/${escapeHtmlAttribute(vm)}`}
              title="Vimeo video"
              loading="lazy"
              allowFullScreen
            />
          </div>
        </div>
      );
    }
    return (
      <div className="ann-block ann-video-fallback">
        <a href={u} rel="noopener noreferrer" target="_blank">
          {u}
        </a>
      </div>
    );
  }
  if (type === 'audio') {
    const au = coerceString(raw.audioUrl ?? raw.audio_url ?? embedUrl) ?? '';
    if (!au || !isSafeUrl(au)) return null;
    return (
      <div className="ann-block ann-audio wi-books-audio">
        <audio controls preload="metadata" src={au} />
      </div>
    );
  }
  if (type === 'footnote') {
    const ref = coerceString(raw.ref ?? raw.footnoteRef) ?? '1';
    const ft = coerceString(raw.text ?? raw.body) ?? '';
    if (!ft) return null;
    const rid = ref.replace(/[^a-zA-Z0-9_-]/g, '') || '1';
    return (
      <p className="ann-block ann-footnote wi-books-footnote" id={`fn-${rid}`}>
        <sup className="ann-fn-ref">{ref}</sup> {ft}
      </p>
    );
  }
  if (type === 'two_column' || type === 'two_col') {
    const left = coerceString(raw.leftText ?? raw.left) ?? '';
    const right = coerceString(raw.rightText ?? raw.right) ?? '';
    if (!left && !right) return null;
    const lr = raw.leftRich === true || raw.left_rich === true;
    const rr = raw.rightRich === true || raw.right_rich === true;
    const lhtml = lr ? inlineMarkdownToHtml(left) : escapeHtml(left).replace(/\n/g, '<br>');
    const rhtml = rr ? inlineMarkdownToHtml(right) : escapeHtml(right).replace(/\n/g, '<br>');
    return (
      <section className="ann-block ann-two-col books-mk-two-col wi-books-two-col">
        <div className="ann-col ann-col-left" dangerouslySetInnerHTML={{ __html: lhtml }} />
        <div className="ann-col ann-col-right" dangerouslySetInnerHTML={{ __html: rhtml }} />
      </section>
    );
  }
  if (type === 'callout') {
    const v = (calloutVariant || 'default').toLowerCase();
    const safeV = ['default', 'info', 'warning', 'success', 'neutral'].includes(v) ? v : 'default';
    return (
      <aside
        className={`ann-block ann-callout books-mk-highlight-box ann-callout--${safeV} wi-books-callout`}
        data-variant={safeV}
      >
        <div className="ann-callout-inner">
          {text.split('\n').map((line, i) => (
            <span key={i}>
              {i > 0 ? <br /> : null}
              {line}
            </span>
          ))}
        </div>
      </aside>
    );
  }
  if (type === 'embed') {
    if (!embedUrl || !isSafeUrl(embedUrl)) return null;
    return (
      <div className="ann-block ann-embed wi-books-embed">
        <a href={embedUrl} rel="noopener noreferrer" target="_blank">
          {embedUrl}
        </a>
      </div>
    );
  }
  if (type === 'button' || type === 'cta') {
    if (btnText && btnUrl && isSafeUrl(btnUrl)) {
      return (
        <div className="ann-block ann-cta-row wi-books-cta">
          {btnUrl.startsWith('/') ? (
            <Link className="btn btn-primary ann-cta" to={btnUrl}>
              {btnText}
            </Link>
          ) : (
            <a className="btn btn-primary ann-cta" href={btnUrl} rel="noopener noreferrer" target="_blank">
              {btnText}
            </a>
          )}
        </div>
      );
    }
    return null;
  }

  return null;
}
